import React, { useState, useEffect, useCallback, useMemo } from "react";
import axios from "axios";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import remarkMath from "remark-math";
import rehypeKatex from "rehype-katex";
import "katex/dist/katex.min.css";
import * as XLSX from "xlsx";
import FlowchartRenderer, { isMermaidCode, isArrowFlow } from "../Agent/FlowchartRenderer";
import DocumentAttachmentList, { parseUserMessageAttachments } from "../Agent/DocumentAttachmentCard";
import { 
  Users, 
  UserCheck, 
  MessageSquare, 
  Cpu, 
  DollarSign, 
  Search,
  Download,
  ThumbsUp,
  ThumbsDown,
  Clock,
  Building,
  RefreshCw,
  AlertCircle,
  ChevronDown,
  ChevronUp,
  Layers,
  Sparkles,
  X,
  Copy,
  Check,
  ArrowUpDown,
  FileText,
  Filter,
  Maximize2,
  Calendar
} from "lucide-react";

const API_BASE = process.env.REACT_APP_API_BASE;
const ADMIN_EMAILS = ["alfa271200@gmail.com"];
const USD_TO_IDR_RATE = 16250;

const INTENT_BADGES = {
  GENERAL_CHAT: { label: "Chat", color: "bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 border-emerald-500/20" },
  LEGAL_SEARCH: { label: "Legal", color: "bg-blue-500/10 text-blue-600 dark:text-blue-400 border-blue-500/20" },
  DOCUMENT_DRAFTING: { label: "Draft", color: "bg-indigo-500/10 text-indigo-600 dark:text-indigo-400 border-indigo-500/20" },
  TAX_CALCULATION: { label: "Calc", color: "bg-amber-500/10 text-amber-600 dark:text-amber-400 border-amber-500/20" },
  DISPUTE_RESPONSE: { label: "Dispute", color: "bg-cyan-500/10 text-cyan-600 dark:text-cyan-400 border-cyan-500/20" },
};

// ─── Mathematical & Tax Expression Preprocessor ──────────────────────────────
export function cleanLatexExpr(expr) {
  return expr
    .replace(/\\text\{([^}]+)\}/g, "$1")
    .replace(/\\mathbf\{([^}]+)\}/g, "$1")
    .replace(/\\mathrm\{([^}]+)\}/g, "$1")
    .replace(/\\frac\{([^}]+)\}\{([^}]+)\}/g, "($1) / ($2)")
    .replace(/\\times/g, "×")
    .replace(/\\div/g, "÷")
    .replace(/\\cdot/g, "·")
    .replace(/\\leq/g, "≤")
    .replace(/\\geq/g, "≥")
    .replace(/\\neq/g, "≠")
    .replace(/\\approx/g, "≈")
    .replace(/\\infty/g, "∞")
    .replace(/\\sqrt\{([^}]+)\}/g, "√($1)")
    .replace(/\\_/g, "_")
    .replace(/\^/g, "^")
    .replace(/\{|\}/g, "")
    .replace(/\s+/g, " ")
    .trim();
}

export function preprocessConsultationText(text) {
  if (typeof text !== "string") return "";

  let cleaned = text.replace(/\r\n/g, "\n");

  // 0. Replace raw HTML <br> tags with double newline
  cleaned = cleaned.replace(/<br\s*\/?>/gi, "\n\n");

  // Strip accidental SVG / pipeline button text artifacts if leaked anywhere in text
  cleaned = cleaned.replace(/(?:svg)?\s*\*{0,2}\s*(?:Analisis|Tanggapan|Kajian|Draf|Kalkulasi|Konsultasi|Bantuan)\s+[^\n*]+—[^\n*]+\*{0,2}\s*(?:\d+(?:\.\d+)?s)?\s*(?:svg)?/gi, "");
  cleaned = cleaned.replace(/svg\s*\*{0,2}[^\n*]+—[^\n*]+\*{0,2}\s*(?:\d+(?:\.\d+)?s)?\s*svg?/gi, "");
  cleaned = cleaned.replace(/(?:\n+)?\s*\*{0,2}(?:Analisis|Tanggapan|Kajian|Draf|Kalkulasi|Konsultasi|Bantuan)[^\n*]+—[^\n*]+\*{0,2}\s*\n+\s*\d+(?:\.\d+)?s\s*$/gi, "");

  // 1. Replace raw unicode bullets (•, ●, ○) with standard markdown list markers
  cleaned = cleaned.replace(/^[ \t]*[•●○]\s*/gm, "- ");

  // 2. Fix dangling numbered list headers where LLM outputs "4.\nTitle:" or "4. \n\nTitle:"
  cleaned = cleaned.replace(/(?:^|\n)\s*(\d+)[\.\)]\s*\n+([A-Za-z0-9\u00C0-\u024F][^\n\r]+)/g, "\n\n$1. $2\n");

  // 4. Highlight executive sections like Kesimpulan, Dasar Hukum, Catatan, Rekomendasi
  cleaned = cleaned.replace(/(?:^|\n)\s*(Kesimpulan|Dasar Hukum|Catatan|Rekomendasi|Perhatian|Langkah Perhitungan|Ringkasan Eksekutif):\s*/gi, "\n\n### $1:\n");

  // 5. Highlight calculation total lines starting with "Total ... = Rp..." as callout blockquotes
  cleaned = cleaned.replace(/(?:^|\n)(?![>\-#*])(Total [^\n=]+=[^\n]+)/g, "\n\n> **$1**\n\n");

  // 6. LaTeX math expressions
  cleaned = cleaned.replace(/\\\[([\s\S]+?)\\\]/g, (_, expr) => `\n\`${cleanLatexExpr(expr)}\`\n`);
  cleaned = cleaned.replace(/\[([^[\]]*\\[^[\]]*)\]/g, (_, expr) => `\`${cleanLatexExpr(expr)}\``);
  cleaned = cleaned.replace(/\\\((.+?)\\\)/g, (_, expr) => `\`${cleanLatexExpr(expr)}\``);

  // 6b. Convert horizontal procedural arrow sequences (e.g. A \rightarrow B \rightarrow C, or A -> B -> C) into flowchart blocks
  cleaned = cleaned.replace(
    /(?:^|\n)\s*(?:\$\$|\\\[)?\s*([^\n$]{3,}?(?:\\rightarrow|\\to|→|-->|->)[^\n$]{3,}?(?:\\rightarrow|\\to|→|-->|->)[^\n$]{2,}?)\s*(?:\$\$|\\\])?(?=\n|$)/gi,
    (match, flowContent) => {
      const cleanFlow = flowContent
        .replace(/\\text\{([^}]+)\}/g, "$1")
        .replace(/\\mathrm\{([^}]+)\}/g, "$1")
        .replace(/\$/g, "")
        .trim();
      const parts = cleanFlow.split(/\s*(?:\\rightarrow|\\to|→|-->|->)\s*/).map((p) => p.trim()).filter(Boolean);
      if (parts.length >= 2) {
        return `\n\n\`\`\`flowchart\n${parts.join("\n↓\n")}\n\`\`\`\n\n`;
      }
      return match;
    }
  );

  // 7. Auto-wrap vertical arrow flow sequences (↓, ⬇, -->, ->) into flowchart blocks
  if (/[↓⬇▼]|(?:^|\n)\s*-->\s*(?:\n|$)/.test(cleaned)) {
    const lines = cleaned.split("\n");
    let inCode = false;
    let inFlow = false;
    let flowLines = [];
    let newLines = [];

    const hasArrowNearby = (idx) => {
      for (let j = Math.max(0, idx - 3); j <= Math.min(lines.length - 1, idx + 3); j++) {
        if (/^\s*[↓⬇▼]\s*$/.test(lines[j]) || /^\s*-->\s*$/.test(lines[j])) return true;
      }
      return false;
    };

    for (let i = 0; i < lines.length; i++) {
      const line = lines[i];
      if (line.trim().startsWith("```")) {
        inCode = !inCode;
        newLines.push(line);
        continue;
      }
      if (inCode) {
        newLines.push(line);
        continue;
      }

      const isArrow = /^\s*[↓⬇▼]\s*$/.test(line) || /^\s*-->\s*$/.test(line) || /^\s*->\s*$/.test(line);

      if (isArrow) {
        inFlow = true;
        flowLines.push(line);
      } else if (inFlow) {
        if (line.trim().startsWith("#") || (line.trim() === "" && !hasArrowNearby(i))) {
          const arrowCount = flowLines.filter(l => /^\s*[↓⬇▼]\s*$/.test(l) || /^\s*-->\s*$/.test(l)).length;
          if (arrowCount >= 2) {
            newLines.push("```flowchart");
            newLines.push(...flowLines);
            newLines.push("```");
          } else {
            newLines.push(...flowLines);
          }
          flowLines = [];
          inFlow = false;
          newLines.push(line);
        } else {
          flowLines.push(line);
        }
      } else if (hasArrowNearby(i) && line.trim().length > 0) {
        const willHaveArrow = (i + 1 < lines.length && /^\s*[↓⬇▼]\s*$/.test(lines[i + 1])) ||
                              (i + 2 < lines.length && /^\s*[↓⬇▼]\s*$/.test(lines[i + 2]));
        if (willHaveArrow) {
          inFlow = true;
          flowLines.push(line);
        } else {
          newLines.push(line);
        }
      } else {
        newLines.push(line);
      }
    }

    if (inFlow) {
      const arrowCount = flowLines.filter(l => /^\s*[↓⬇▼]\s*$/.test(l) || /^\s*-->\s*$/.test(l)).length;
      if (arrowCount >= 2) {
        newLines.push("```flowchart");
        newLines.push(...flowLines);
        newLines.push("```");
      } else {
        newLines.push(...flowLines);
      }
    }

    cleaned = newLines.join("\n");
  }

  // 8. Clean excessive blank lines
  cleaned = cleaned.replace(/\n{3,}/g, "\n\n");

  return cleaned.trim();
}

// ─── Executive Typography Markdown Components ─────────────────────────────────
const markdownComponents = {
  p: ({ children }) => (
    <p className="text-text-pri text-[14.5px] sm:text-[15px] leading-[1.8] mb-3.5 last:mb-0 font-normal text-justify">
      {children}
    </p>
  ),
  strong: ({ children }) => (
    <strong className="font-semibold text-text-pri text-[inherit]">
      {children}
    </strong>
  ),
  em: ({ children }) => (
    <em className="italic text-text-sec text-[inherit]">{children}</em>
  ),
  h1: ({ children }) => (
    <h1 className="text-lg sm:text-xl font-bold text-brand mt-5 mb-2.5 pb-2 border-b border-ledger-border">
      {children}
    </h1>
  ),
  h2: ({ children }) => (
    <h2 className="text-base sm:text-lg font-bold text-text-pri mt-4 mb-2">
      {children}
    </h2>
  ),
  h3: ({ children }) => (
    <h3 className="text-[15px] sm:text-[16px] font-bold text-brand mt-4 mb-2 flex items-center gap-2">
      <span className="w-1.5 h-4 bg-brand rounded-full inline-block shrink-0"></span>
      <span>{children}</span>
    </h3>
  ),
  ul: ({ children }) => (
    <ul className="list-disc list-outside ml-6 space-y-2 my-3 text-[14.5px] sm:text-[15px] leading-[1.8] text-text-pri">
      {children}
    </ul>
  ),
  ol: ({ start, children }) => (
    <ol start={start} className="list-decimal list-outside ml-6 space-y-2 my-3 text-[14.5px] sm:text-[15px] leading-[1.8] text-text-pri">
      {children}
    </ol>
  ),
  li: ({ children }) => (
    <li className="text-[14.5px] sm:text-[15px] leading-[1.8] text-text-pri pl-1 marker:text-brand [&>p]:!my-0.5 text-justify">
      {children}
    </li>
  ),
  code: ({ node, className, children, ...props }) => {
    const isBlock = node?.tagName === "code" && node?.properties?.className;
    const match = /language-(\w+)/.exec(className || "");
    const lang = match ? match[1].toLowerCase() : "";
    const rawText = Array.isArray(children) ? children.join("") : String(children || "");

    if (lang === "mermaid" || isMermaidCode(rawText)) {
      return <FlowchartRenderer content={rawText} />;
    }
    if (lang === "flowchart" || isArrowFlow(rawText)) {
      return <FlowchartRenderer content={rawText} />;
    }

    return !isBlock ? (
      <code className="bg-paper text-brand px-1.5 py-0.5 rounded font-mono text-[13px] border border-ledger-border font-semibold" {...props}>
        {children}
      </code>
    ) : (
      <code className="block bg-paper text-text-pri p-3.5 rounded-xl font-mono text-[13px] border border-ledger-border overflow-x-auto my-3 leading-relaxed" {...props}>
        {children}
      </code>
    );
  },
  pre: ({ children }) => {
    const extractRaw = (node) => {
      if (!node) return "";
      if (typeof node === "string") return node;
      if (Array.isArray(node)) return node.map(extractRaw).join("");
      if (node?.props?.children) return extractRaw(node.props.children);
      return String(node);
    };

    const rawText = extractRaw(children);
    if (isMermaidCode(rawText) || isArrowFlow(rawText)) {
      return <FlowchartRenderer content={rawText} />;
    }

    return (
      <pre className="bg-paper border border-ledger-border rounded-xl p-3 my-3 overflow-x-auto font-mono text-[13px]">
        {children}
      </pre>
    );
  },
  blockquote: ({ children }) => (
    <blockquote className="border-l-4 border-brand bg-brand-low/50 px-4 py-3 rounded-r-xl my-3.5 text-text-pri font-medium shadow-2xs text-justify">
      {children}
    </blockquote>
  ),
  table: ({ children }) => (
    <div className="overflow-x-auto my-4 rounded-xl border border-ledger-border bg-paper shadow-2xs">
      <table className="w-full text-xs sm:text-[13px] text-left divide-y divide-ledger-border">
        {children}
      </table>
    </div>
  ),
  thead: ({ children }) => (
    <thead className="bg-card-bg text-text-sec uppercase tracking-wider text-[11px] font-semibold">
      {children}
    </thead>
  ),
  tbody: ({ children }) => (
    <tbody className="divide-y divide-ledger-border/40">
      {children}
    </tbody>
  ),
  tr: ({ children }) => (
    <tr className="hover:bg-surface-hover/50 transition-colors">
      {children}
    </tr>
  ),
  th: ({ children }) => (
    <th className="px-4 py-2.5 whitespace-nowrap font-semibold">
      {children}
    </th>
  ),
  td: ({ children }) => (
    <td className="px-4 py-2.5 font-mono whitespace-nowrap text-text-pri">
      {children}
    </td>
  ),
  hr: () => <hr className="border-ledger-border my-4" />,
};

export default function UserAnalytics() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState(null);
  const [isAdmin, setIsAdmin] = useState(false);
  
  // Drawer & Inspection State
  const [drawerUser, setDrawerUser] = useState(null);
  const [selectedUser, setSelectedUser] = useState(null);
  const [chatHistory, setChatHistory] = useState([]);
  const [chatLoading, setChatLoading] = useState(false);
  
  // Search & Filters
  const [searchTerm, setSearchTerm] = useState("");
  const [sortOption, setSortOption] = useState("recent_chat");
  const [exporting, setExporting] = useState(false);
  const [showExportModal, setShowExportModal] = useState(false);
  const [exportStartDate, setExportStartDate] = useState("");
  const [exportEndDate, setExportEndDate] = useState("");
  const [exportSelectedUserId, setExportSelectedUserId] = useState("");
  const [currency, setCurrency] = useState("USD");

  useEffect(() => {
    const token = localStorage.getItem("token");
    if (token) {
      try {
        const payload = JSON.parse(atob(token.split(".")[1]));
        setIsAdmin(ADMIN_EMAILS.includes((payload.email || "").toLowerCase()));
      } catch {
        setIsAdmin(false);
      }
    }
  }, []);

  const fetchData = async (isManual = false) => {
    if (isManual) setRefreshing(true);
    else setLoading(true);
    try {
      const token = localStorage.getItem("token");
      const res = await axios.get(`${API_BASE}/agent/admin/user-analytics`, {
        headers: { Authorization: `Bearer ${token}` },
        timeout: 30000,
      });
      setData(res.data);
      setError(null);
    } catch (err) {
      setError(err?.response?.data?.message || err.message);
    } finally {
      setLoading(false);
      if (isManual) setRefreshing(false);
    }
  };

  useEffect(() => {
    if (isAdmin) {
      fetchData();
    }
  }, [isAdmin]);

  const loadChatHistory = useCallback(async (userId) => {
    setChatLoading(true);
    try {
      const token = localStorage.getItem("token");
      const res = await axios.get(`${API_BASE}/agent/admin/user-chat-history/${userId}`, {
        headers: { Authorization: `Bearer ${token}` },
        timeout: 30000,
      });
      setChatHistory(res.data.messages || []);
    } catch (err) {
      setChatHistory([]);
    } finally {
      setChatLoading(false);
    }
  }, []);

  const openDrawer = (user) => {
    setDrawerUser(user);
    loadChatHistory(user.id);
  };

  const closeDrawer = () => {
    setDrawerUser(null);
  };

  const toggleInlineChat = (userId) => {
    if (selectedUser === userId) {
      setSelectedUser(null);
      setChatHistory([]);
    } else {
      setSelectedUser(userId);
      loadChatHistory(userId);
    }
  };

  const formatCost = (valUsd) => {
    const num = Number(valUsd || 0);
    if (currency === "IDR") {
      const idr = num * USD_TO_IDR_RATE;
      return `Rp ${Math.round(idr).toLocaleString("id-ID")}`;
    }
    return `$${num.toFixed(4)}`;
  };

  const setExportPreset = (type) => {
    const today = new Date();
    const toISO = (d) => {
      const year = d.getFullYear();
      const month = String(d.getMonth() + 1).padStart(2, '0');
      const day = String(d.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    };
    if (type === 'today') {
      setExportStartDate(toISO(today));
      setExportEndDate(toISO(today));
    } else if (type === '7days') {
      const past = new Date(today);
      past.setDate(past.getDate() - 7);
      setExportStartDate(toISO(past));
      setExportEndDate(toISO(today));
    } else if (type === '30days') {
      const past = new Date(today);
      past.setDate(past.getDate() - 30);
      setExportStartDate(toISO(past));
      setExportEndDate(toISO(today));
    } else if (type === 'month') {
      const firstDay = new Date(today.getFullYear(), today.getMonth(), 1);
      setExportStartDate(toISO(firstDay));
      setExportEndDate(toISO(today));
    } else if (type === 'all') {
      setExportStartDate('');
      setExportEndDate('');
    }
  };

  const handleExecuteExport = async () => {
    try {
      setExporting(true);
      const token = localStorage.getItem("token");
      const params = {};
      if (exportStartDate) params.startDate = exportStartDate;
      if (exportEndDate) params.endDate = exportEndDate;
      if (exportSelectedUserId) params.userId = exportSelectedUserId;

      const res = await axios.get(`${API_BASE}/agent/admin/export-chat-history`, {
        headers: { Authorization: `Bearer ${token}` },
        params,
        timeout: 60000,
      });
      
      const chatData = res.data.data;
      if (!chatData || chatData.length === 0) {
        const rangeMsg = exportStartDate || exportEndDate
          ? `dari ${exportStartDate || 'awal'} s.d. ${exportEndDate || 'terbaru'}`
          : "pada sistem";
        alert(`Tidak ada data riwayat chat ${rangeMsg} untuk diunduh.`);
        return;
      }

      const userChats = {};
      chatData.forEach(chat => {
        const key = chat.user_email || chat.user_name || "unknown";
        if (!userChats[key]) userChats[key] = [];
        userChats[key].push(chat);
      });

      const safeCell = (val) => {
        if (!val) return '-';
        const str = String(val).trim();
        return str.length > 32000 ? str.slice(0, 32000) + '... [terpotong batas sel Excel]' : str;
      };

      let pairedData = [];

      const formatIsoWib = (dtVal) => {
        if (!dtVal) return '';
        const d = new Date(dtVal);
        if (Number.isNaN(d.getTime())) return String(dtVal);
        const wibMs = d.getTime() + 7 * 3600 * 1000;
        const wib = new Date(wibMs);
        const pad = (n, len = 2) => String(n).padStart(len, '0');
        return `${wib.getUTCFullYear()}-${pad(wib.getUTCMonth() + 1)}-${pad(wib.getUTCDate())} ${pad(wib.getUTCHours())}:${pad(wib.getUTCMinutes())}:${pad(wib.getUTCSeconds())}.${pad(wib.getUTCMilliseconds(), 3)} +07:00`;
      };

      const normalizeCompany = (c) => {
        const str = String(c || '').trim();
        if (!str || str === '-') return '-';
        if (str.toLowerCase() === 'enforcea') return 'EnforceA';
        return str.charAt(0).toUpperCase() + str.slice(1);
      };

      const computeSafeLatency = (ansChat, qChat) => {
        if (ansChat.response_ms !== undefined && ansChat.response_ms !== null && ansChat.response_ms !== 1000) {
          return Number(ansChat.response_ms);
        }
        if (qChat?.created_at && ansChat?.created_at) {
          const diff = new Date(ansChat.created_at).getTime() - new Date(qChat.created_at).getTime();
          if (diff > 0 && diff !== 1000) {
            return diff;
          }
        }
        return null;
      };

      Object.values(userChats).forEach(chats => {
        let currentQuestion = null;
        chats.forEach(chat => {
          const sender = (chat.sender || "").toLowerCase();
          const isUser = sender === 'user' || sender === 'human';
          const isAssistant = sender === 'ai' || sender === 'assistant' || sender === 'bot';

          if (isUser) {
            if (currentQuestion) {
              const computedMs = computeSafeLatency(chat, currentQuestion);
              pairedData.push({
                Session_ID: chat.session_id || currentQuestion.session_id || '-',
                User_Name: currentQuestion.user_name || '-',
                User_Email: currentQuestion.user_email || '-',
                User_Company: normalizeCompany(currentQuestion.user_company || chat.user_company),
                Intent: chat.intent && chat.intent !== '-' ? chat.intent : (currentQuestion.intent && currentQuestion.intent !== '-' ? currentQuestion.intent : 'CONSULTATION'),
                Doc_Type: chat.doc_type && chat.doc_type !== '-' && chat.doc_type !== 'consultation' ? chat.doc_type : '',
                Tipe_Respons: chat.response_type || 'KONSULTASI',
                Status_Verifikasi: chat.verification_status || '-',
                File_Generated: chat.file_generated || '',
                Lampiran_OCR: chat.attachment_info || currentQuestion.attachment_info || '',
                Sitasi: chat.citations || '',
                Feedback_User: chat.user_feedback || '',
                Waktu_Tanya: formatIsoWib(currentQuestion.created_at),
                Pertanyaan: safeCell(currentQuestion.message),
                Jawaban_Tacia: safeCell(chat.message),
                Waktu_Jawab: formatIsoWib(chat.created_at),
                Latency_ms: computedMs,
              });
              currentQuestion = null;
            } else if (false) {
              pairedData.push({
                Session_ID: currentQuestion.session_id || '-',
                User_Name: currentQuestion.user_name || '-',
                User_Email: currentQuestion.user_email || '-',
                User_Company: currentQuestion.user_company || '-',
                Intent: currentQuestion.intent || '-',
                Doc_Type: '-',
                Status_Verifikasi: '-',
                Waktu_Tanya: currentQuestion.created_at ? new Date(currentQuestion.created_at).toLocaleString('id-ID') : '-',
                Pertanyaan: safeCell(currentQuestion.message),
                Jawaban_Tacia: '-',
                Waktu_Jawab: '-',
                Latency_ms: '-',
              });
            }
            currentQuestion = chat;
          } else if (isAssistant) {
            if (currentQuestion) {
              const computedMs = computeSafeLatency(chat, currentQuestion);
              pairedData.push({
                Session_ID: chat.session_id || currentQuestion.session_id || '-',
                User_Name: currentQuestion.user_name || '-',
                User_Email: currentQuestion.user_email || '-',
                User_Company: normalizeCompany(currentQuestion.user_company || chat.user_company),
                Intent: chat.intent && chat.intent !== '-' ? chat.intent : (currentQuestion.intent && currentQuestion.intent !== '-' ? currentQuestion.intent : 'CONSULTATION'),
                Doc_Type: chat.doc_type && chat.doc_type !== '-' && chat.doc_type !== 'consultation' ? chat.doc_type : '',
                Tipe_Respons: chat.response_type || 'KONSULTASI',
                Status_Verifikasi: chat.verification_status || '-',
                File_Generated: chat.file_generated || '',
                Lampiran_OCR: chat.attachment_info || currentQuestion.attachment_info || '',
                Sitasi: chat.citations || '',
                Feedback_User: chat.user_feedback || '',
                Waktu_Tanya: formatIsoWib(currentQuestion.created_at),
                Pertanyaan: safeCell(currentQuestion.message),
                Jawaban_Tacia: safeCell(chat.message),
                Waktu_Jawab: formatIsoWib(chat.created_at),
                Latency_ms: computedMs,
              });
              currentQuestion = null;
            } else if (false) {
              const computedMs = (chat.response_ms !== undefined && chat.response_ms !== null)
                ? chat.response_ms
                : (currentQuestion.created_at && chat.created_at
                    ? Math.max(0, new Date(chat.created_at).getTime() - new Date(currentQuestion.created_at).getTime())
                    : '-');
              pairedData.push({
                Session_ID: chat.session_id || currentQuestion.session_id || '-',
                User_Name: currentQuestion.user_name || '-',
                User_Email: currentQuestion.user_email || '-',
                User_Company: currentQuestion.user_company || '-',
                Intent: chat.intent || currentQuestion.intent || '-',
                Doc_Type: chat.doc_type || '-',
                Status_Verifikasi: chat.verification_status || '-',
                Waktu_Tanya: currentQuestion.created_at ? new Date(currentQuestion.created_at).toLocaleString('id-ID') : '-',
                Pertanyaan: safeCell(currentQuestion.message),
                Jawaban_Tacia: safeCell(chat.message),
                Waktu_Jawab: chat.created_at ? new Date(chat.created_at).toLocaleString('id-ID') : '-',
                Latency_ms: computedMs,
              });
              currentQuestion = null;
            } else {
              pairedData.push({
                Session_ID: chat.session_id || '-',
                User_Name: chat.user_name || '-',
                User_Email: chat.user_email || '-',
                User_Company: chat.user_company || '-',
                Intent: chat.intent || '-',
                Doc_Type: chat.doc_type || '-',
                Status_Verifikasi: chat.verification_status || '-',
                Waktu_Tanya: '-',
                Pertanyaan: '-',
                Jawaban_Tacia: safeCell(chat.message),
                Waktu_Jawab: chat.created_at ? new Date(chat.created_at).toLocaleString('id-ID') : '-',
                Latency_ms: chat.response_ms ?? '-',
              });
            }
          }
        });

        if (currentQuestion) {
          pairedData.push({
            Session_ID: currentQuestion.session_id || '-',
            User_Name: currentQuestion.user_name || '-',
            User_Email: currentQuestion.user_email || '-',
            User_Company: currentQuestion.user_company || '-',
            Intent: currentQuestion.intent || '-',
            Doc_Type: '-',
            Status_Verifikasi: '-',
            Waktu_Tanya: currentQuestion.created_at ? new Date(currentQuestion.created_at).toLocaleString('id-ID') : '-',
            Pertanyaan: safeCell(currentQuestion.message),
            Jawaban_Tacia: '-',
            Waktu_Jawab: '-',
            Latency_ms: '-',
          });
        }
      });

      const worksheet = XLSX.utils.json_to_sheet(pairedData);
      if (worksheet['!ref']) {
        worksheet['!autofilter'] = { ref: worksheet['!ref'] };
      }
      worksheet['!views'] = [{ state: 'frozen', ySplit: 1 }];
      Object.keys(worksheet).forEach(cellKey => {
        if (!cellKey.startsWith('!') && worksheet[cellKey] && typeof worksheet[cellKey] === 'object') {
          worksheet[cellKey].s = {
            alignment: { wrapText: true, vertical: 'top' },
          };
        }
      });
      worksheet['!cols'] = [
        { wch: 24 }, { wch: 18 }, { wch: 26 }, { wch: 18 },
        { wch: 18 }, { wch: 16 }, { wch: 22 }, { wch: 20 },
        { wch: 45 }, { wch: 65 }, { wch: 20 }, { wch: 12 }
      ];

      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, "Riwayat Chat");
      
      const rangeTag = exportStartDate || exportEndDate
        ? `${exportStartDate || 'awal'}_sd_${exportEndDate || 'terbaru'}`
        : `semua_${new Date().toISOString().slice(0, 10)}`;
      XLSX.writeFile(workbook, `Tacia_Chat_History_${rangeTag}.xlsx`);
      setShowExportModal(false);
    } catch (err) {
      console.error("Gagal mengekspor data:", err);
      const msg = err?.response?.data?.message || err.message || "Gagal mengunduh Excel. Silakan coba lagi.";
      alert(`Gagal mengunduh Excel: ${msg}`);
    } finally {
      setExporting(false);
    }
  };

  if (!isAdmin) {
    return (
      <div className="flex items-center justify-center min-h-[80vh] p-6">
        <div className="text-center p-8 max-w-md bg-card-bg border border-ledger-border rounded-2xl shadow-lg">
          <div className="w-12 h-12 rounded-full bg-red-500/10 text-red-500 flex items-center justify-center mx-auto mb-4 border border-red-500/20">
            <AlertCircle className="w-6 h-6" />
          </div>
          <h2 className="text-xl font-bold text-text-pri">Akses Khusus Admin</h2>
          <p className="text-text-sec text-sm mt-2 leading-relaxed">
            Halaman ini khusus untuk pemantauan pengguna dan analitik konsultasi TACIA.
          </p>
          <a
            href="/dashboard"
            className="inline-flex items-center gap-2 mt-5 px-5 py-2.5 bg-brand text-white rounded-xl text-xs font-semibold hover:bg-brand/90 transition shadow-sm"
          >
            Kembali ke Dashboard
          </a>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[80vh] gap-3">
        <div className="animate-spin w-8 h-8 border-3 border-brand border-t-transparent rounded-full" />
        <span className="text-xs font-medium text-text-sec">Memuat analitik pengguna...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center min-h-[80vh] p-6">
        <div className="text-center p-8 max-w-md bg-card-bg border border-ledger-border rounded-2xl shadow-lg">
          <div className="w-12 h-12 rounded-full bg-rose-500/10 text-rose-500 flex items-center justify-center mx-auto mb-4 border border-rose-500/20">
            <AlertCircle className="w-6 h-6" />
          </div>
          <h2 className="text-lg font-bold text-text-pri">Gagal Mengambil Data</h2>
          <p className="text-text-sec text-xs mt-2">{error}</p>
          <button
            onClick={() => fetchData(true)}
            className="inline-flex items-center gap-2 mt-4 px-4 py-2 bg-brand text-white rounded-xl text-xs font-medium hover:bg-brand/90 transition shadow-sm"
          >
            <RefreshCw className="w-3.5 h-3.5" />
            Coba Lagi
          </button>
        </div>
      </div>
    );
  }

  const { summary, users } = data || {};
  const s = summary || {};

  const filteredUsers = (users || []).filter(u => {
    if (!searchTerm) return true;
    const term = searchTerm.toLowerCase();
    return (
      (u.name || "").toLowerCase().includes(term) ||
      (u.email || "").toLowerCase().includes(term) ||
      (u.company || "").toLowerCase().includes(term)
    );
  }).sort((a, b) => {
    const sa = a.stats || {};
    const sb = b.stats || {};
    if (sortOption === "recent_chat" || sortOption === "latest") {
      const dateA = new Date(sa.last_chat_at || sa.last_active || 0).getTime();
      const dateB = new Date(sb.last_chat_at || sb.last_active || 0).getTime();
      return dateB - dateA;
    }
    if (sortOption === "most_queries") {
      return (sb.total_queries || 0) - (sa.total_queries || 0);
    }
    return 0;
  });

  return (
    <div className="min-h-full bg-paper p-4 sm:p-8 overflow-y-auto space-y-6">
      {/* Top Header */}
      <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4 border-b border-ledger-border pb-6">
        <div>
          <div className="inline-flex items-center gap-2 px-2.5 py-1 rounded-full bg-brand-low border border-brand/20 text-brand text-[10px] font-bold uppercase tracking-wider mb-2">
            <Users className="w-3 h-3" />
            <span>TACIA User & Engagement Telemetry</span>
          </div>
          <h1 className="text-2xl sm:text-3xl font-bold text-text-pri tracking-tight">
            User Analytics
          </h1>
          <p className="text-text-sec text-xs sm:text-sm mt-1">
            Pantau aktivitas konsultasi, konsumsi token per pengguna, dan transkrip percakapan pajak secara mendalam.
          </p>
        </div>

        <div className="flex items-center flex-wrap gap-2.5">
          <div className="flex items-center bg-card-bg border border-ledger-border rounded-xl p-0.5 shadow-2xs">
            <button
              onClick={() => setCurrency("USD")}
              className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition ${
                currency === "USD"
                  ? "bg-brand text-white shadow-xs"
                  : "text-text-sec hover:text-text-pri"
              }`}
            >
              USD ($)
            </button>
            <button
              onClick={() => setCurrency("IDR")}
              className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition ${
                currency === "IDR"
                  ? "bg-brand text-white shadow-xs"
                  : "text-text-sec hover:text-text-pri"
              }`}
            >
              IDR (Rp)
            </button>
          </div>

          <button
            onClick={() => {
              if (!exportStartDate && !exportEndDate) {
                setExportPreset('30days');
              }
              setShowExportModal(true);
            }}
            disabled={exporting}
            className="inline-flex items-center gap-1.5 px-3.5 py-2 bg-card-bg border border-ledger-border hover:border-brand/40 text-text-pri rounded-xl text-xs font-semibold transition shadow-2xs hover:bg-surface-hover disabled:opacity-60 cursor-pointer"
            title="Unduh Riwayat Percakapan (Excel)"
          >
            <Download className="w-3.5 h-3.5 text-brand" />
            <span>{exporting ? "Menyiapkan..." : "Export XLS"}</span>
          </button>

          <button
            onClick={() => fetchData(true)}
            disabled={refreshing}
            className="inline-flex items-center gap-1.5 px-3.5 py-2 bg-card-bg border border-ledger-border hover:border-brand/40 text-text-pri rounded-xl text-xs font-semibold transition shadow-2xs hover:bg-surface-hover disabled:opacity-60"
            title="Refresh Data"
          >
            <RefreshCw className={`w-3.5 h-3.5 text-text-sec ${refreshing ? "animate-spin text-brand" : ""}`} />
            <span>{refreshing ? "Menyinkron..." : "Refresh"}</span>
          </button>
        </div>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
        <div className="bg-card-bg border border-ledger-border rounded-2xl p-4 shadow-2xs flex flex-col justify-between group hover:border-brand/30 transition-all">
          <div className="flex items-center justify-between">
            <span className="text-[10px] font-bold uppercase tracking-wider text-text-sec">Total Pengguna</span>
            <div className="w-7 h-7 rounded-lg bg-brand-low border border-brand/20 text-brand flex items-center justify-center">
              <Users className="w-3.5 h-3.5" />
            </div>
          </div>
          <div className="mt-3">
            <div className="text-xl sm:text-2xl font-bold text-text-pri tracking-tight">{s.total_users || 0}</div>
            <span className="text-[10px] text-text-sec">Akun terdaftar</span>
          </div>
        </div>

        <div className="bg-card-bg border border-ledger-border rounded-2xl p-4 shadow-2xs flex flex-col justify-between group hover:border-emerald-500/30 transition-all">
          <div className="flex items-center justify-between">
            <span className="text-[10px] font-bold uppercase tracking-wider text-text-sec">Pengguna Aktif</span>
            <div className="w-7 h-7 rounded-lg bg-emerald-500/10 border border-emerald-500/20 text-emerald-500 flex items-center justify-center">
              <UserCheck className="w-3.5 h-3.5" />
            </div>
          </div>
          <div className="mt-3">
            <div className="text-xl sm:text-2xl font-bold text-emerald-600 dark:text-emerald-400 tracking-tight">{s.active_users || 0}</div>
            <span className="text-[10px] text-text-sec">Memiliki kueri aktif</span>
          </div>
        </div>

        <div className="bg-card-bg border border-ledger-border rounded-2xl p-4 shadow-2xs flex flex-col justify-between group hover:border-blue-500/30 transition-all">
          <div className="flex items-center justify-between">
            <span className="text-[10px] font-bold uppercase tracking-wider text-text-sec">Total Kueri</span>
            <div className="w-7 h-7 rounded-lg bg-blue-500/10 border border-blue-500/20 text-blue-500 flex items-center justify-center">
              <MessageSquare className="w-3.5 h-3.5" />
            </div>
          </div>
          <div className="mt-3">
            <div className="text-xl sm:text-2xl font-bold text-text-pri tracking-tight">{(s.total_queries || 0).toLocaleString()}</div>
            <span className="text-[10px] text-text-sec">Pertanyaan dikirim</span>
          </div>
        </div>

        <div className="bg-card-bg border border-ledger-border rounded-2xl p-4 shadow-2xs flex flex-col justify-between group hover:border-indigo-500/30 transition-all">
          <div className="flex items-center justify-between">
            <span className="text-[10px] font-bold uppercase tracking-wider text-text-sec">Total Token</span>
            <div className="w-7 h-7 rounded-lg bg-indigo-500/10 border border-indigo-500/20 text-indigo-500 flex items-center justify-center">
              <Cpu className="w-3.5 h-3.5" />
            </div>
          </div>
          <div className="mt-3">
            <div className="text-xl sm:text-2xl font-bold text-text-pri tracking-tight">
              {(Number(s.total_tokens || 0) >= 1_000_000)
                ? `${(Number(s.total_tokens || 0) / 1_000_000).toFixed(2)}M`
                : (s.total_tokens || 0).toLocaleString()}
            </div>
            <span className="text-[10px] text-text-sec">Token LLM terpakai</span>
          </div>
        </div>

        <div className="bg-card-bg border border-ledger-border rounded-2xl p-4 shadow-2xs flex flex-col justify-between group hover:border-emerald-500/30 transition-all">
          <div className="flex items-center justify-between">
            <span className="text-[10px] font-bold uppercase tracking-wider text-text-sec">Total Biaya</span>
            <div className="w-7 h-7 rounded-lg bg-emerald-500/10 border border-emerald-500/20 text-emerald-500 flex items-center justify-center">
              <DollarSign className="w-3.5 h-3.5" />
            </div>
          </div>
          <div className="mt-3">
            <div className="text-xl sm:text-2xl font-bold text-emerald-600 dark:text-emerald-400 tracking-tight">
              {formatCost(s.total_cost_usd)}
            </div>
            <span className="text-[10px] text-text-sec">Estimasi pengeluaran</span>
          </div>
        </div>
      </div>

      {/* Search & Sort Filters */}
      <div className="flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1">
          <Search className="w-4 h-4 absolute left-3.5 top-1/2 -translate-y-1/2 text-text-sec" />
          <input
            type="text"
            placeholder="Cari nama pengguna, alamat email, atau perusahaan..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-4 py-2.5 rounded-xl border border-ledger-border bg-input-bg text-xs sm:text-sm text-text-pri placeholder:text-muted focus:outline-none focus:ring-1 focus:ring-brand shadow-2xs transition"
          />
        </div>

        <div className="w-full sm:w-60">
          <select
            value={sortOption}
            onChange={(e) => setSortOption(e.target.value)}
            className="w-full px-3.5 py-2.5 rounded-xl border border-ledger-border bg-input-bg text-xs sm:text-sm text-text-pri focus:outline-none focus:ring-1 focus:ring-brand shadow-2xs transition cursor-pointer font-medium"
          >
            <option value="recent_chat">Urut: Chat Terbaru</option>
            <option value="most_queries">Urut: Kueri Terbanyak</option>
          </select>
        </div>
      </div>

      {/* Users Data Table */}
      <div className="bg-card-bg rounded-2xl border border-ledger-border overflow-hidden shadow-2xs">
        <div className="overflow-x-auto">
          <table className="w-full text-xs text-left">
            <thead className="bg-paper text-text-sec font-semibold border-b border-ledger-border">
              <tr>
                <th className="py-3 px-4">Pengguna</th>
                <th className="py-3 px-3 text-center">Kueri</th>
                <th className="py-3 px-3 text-center">Pesan</th>
                <th className="py-3 px-3 text-center">Token</th>
                <th className="py-3 px-3 text-center">Biaya</th>
                <th className="py-3 px-3 text-center">Kepuasan</th>
                <th className="py-3 px-3">Distribusi Intent</th>
                <th className="py-3 px-3">Chat Terakhir</th>
                <th className="py-3 px-4 text-center">Transkrip</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-ledger-border/40">
              {filteredUsers.map((user) => (
                <UserRow
                  key={user.id}
                  user={user}
                  isExpanded={selectedUser === user.id}
                  onToggleInline={() => toggleInlineChat(user.id)}
                  onOpenDrawer={() => openDrawer(user)}
                  chatHistory={selectedUser === user.id ? chatHistory : []}
                  chatLoading={selectedUser === user.id && chatLoading}
                  formatCost={formatCost}
                />
              ))}
              {filteredUsers.length === 0 && (
                <tr>
                  <td colSpan={9} className="py-12 text-center text-text-sec">
                    {searchTerm ? "Tidak ada pengguna yang cocok dengan kriteria pencarian" : "Belum ada data pengguna"}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Dedicated Executive Consultation Reader Drawer */}
      {drawerUser && (
        <ConsultationReaderDrawer
          user={drawerUser}
          onClose={closeDrawer}
          chatHistory={chatHistory}
          chatLoading={chatLoading}
          formatCost={formatCost}
        />
      )}

      {/* Export Date Range Modal */}
      {showExportModal && (
        <div 
          className="fixed inset-0 z-50 bg-black/60 backdrop-blur-xs flex items-center justify-center p-4 animate-in fade-in duration-150"
          onClick={() => !exporting && setShowExportModal(false)}
        >
          <div 
            className="bg-card-bg border border-ledger-border rounded-2xl shadow-2xl max-w-lg w-full p-6 space-y-5 animate-in zoom-in-95 duration-150"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-start justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-brand-low border border-brand/20 text-brand flex items-center justify-center">
                  <Download className="w-5 h-5" />
                </div>
                <div>
                  <h3 className="text-base font-bold text-text-pri">Unduh Riwayat Chat (Excel)</h3>
                  <p className="text-xs text-text-sec mt-0.5">Pilih rentang tanggal untuk ekspor berkas spreadsheet (.xlsx)</p>
                </div>
              </div>
              <button 
                onClick={() => setShowExportModal(false)}
                disabled={exporting}
                className="p-1.5 rounded-lg text-text-sec hover:text-text-pri hover:bg-surface-hover transition cursor-pointer"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            <div>
              <label className="block text-[11px] font-bold text-text-sec uppercase tracking-wider mb-2">
                Pilihan Cepat Rentang Waktu
              </label>
              <div className="flex flex-wrap gap-1.5">
                {[
                  { id: "today", label: "Hari Ini" },
                  { id: "7days", label: "7 Hari Terakhir" },
                  { id: "30days", label: "30 Hari Terakhir" },
                  { id: "month", label: "Bulan Ini" },
                  { id: "all", label: "Semua Riwayat" },
                ].map(p => (
                  <button
                    key={p.id}
                    type="button"
                    onClick={() => setExportPreset(p.id)}
                    disabled={exporting}
                    className="px-2.5 py-1.5 rounded-lg text-xs font-medium border border-ledger-border bg-paper hover:bg-surface-hover hover:border-brand/40 text-text-pri transition cursor-pointer"
                  >
                    {p.label}
                  </button>
                ))}
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-[11px] font-semibold text-text-sec mb-1">
                  Dari Tanggal
                </label>
                <input
                  type="date"
                  value={exportStartDate}
                  onChange={(e) => setExportStartDate(e.target.value)}
                  disabled={exporting}
                  className="w-full px-3 py-2 rounded-xl border border-ledger-border bg-input-bg text-xs sm:text-sm text-text-pri focus:outline-none focus:ring-1 focus:ring-brand shadow-2xs transition"
                />
              </div>
              <div>
                <label className="block text-[11px] font-semibold text-text-sec mb-1">
                  Sampai Tanggal
                </label>
                <input
                  type="date"
                  value={exportEndDate}
                  onChange={(e) => setExportEndDate(e.target.value)}
                  disabled={exporting}
                  className="w-full px-3 py-2 rounded-xl border border-ledger-border bg-input-bg text-xs sm:text-sm text-text-pri focus:outline-none focus:ring-1 focus:ring-brand shadow-2xs transition"
                />
              </div>
            </div>

            <div>
              <label className="block text-[11px] font-semibold text-text-sec mb-1">
                Filter Pengguna (Opsional)
              </label>
              <select
                value={exportSelectedUserId}
                onChange={(e) => setExportSelectedUserId(e.target.value)}
                disabled={exporting}
                className="w-full px-3 py-2 rounded-xl border border-ledger-border bg-input-bg text-xs sm:text-sm text-text-pri focus:outline-none focus:ring-1 focus:ring-brand shadow-2xs transition cursor-pointer font-medium"
              >
                <option value="">Semua Pengguna</option>
                {(data?.users || []).map(u => (
                  <option key={u.id} value={u.id}>
                    {u.name || u.email} {u.company ? `(${u.company})` : ""}
                  </option>
                ))}
              </select>
            </div>

            <div className="p-3 rounded-xl bg-paper border border-ledger-border text-[11px] text-text-sec space-y-1">
              <p className="font-semibold text-text-pri flex items-center gap-1.5">
                <Calendar className="w-3.5 h-3.5 text-brand" />
                <span>Format Data Spreadsheet</span>
              </p>
              <p>
                Data akan memuat pasangan Pertanyaan User & Jawaban TACIA AI secara kronologis per baris, lengkap dengan cap waktu dan nama penanya.
              </p>
            </div>

            <div className="flex items-center justify-end gap-2.5 pt-2">
              <button
                type="button"
                onClick={() => setShowExportModal(false)}
                disabled={exporting}
                className="px-4 py-2 rounded-xl text-xs font-semibold border border-ledger-border bg-card-bg text-text-sec hover:text-text-pri hover:bg-surface-hover transition cursor-pointer"
              >
                Batal
              </button>
              <button
                type="button"
                onClick={handleExecuteExport}
                disabled={exporting}
                className="inline-flex items-center gap-2 px-5 py-2 rounded-xl text-xs font-semibold bg-brand text-white hover:bg-brand/90 transition shadow-xs cursor-pointer disabled:opacity-60"
              >
                {exporting ? (
                  <>
                    <RefreshCw className="w-3.5 h-3.5 animate-spin" />
                    <span>Menyiapkan Excel...</span>
                  </>
                ) : (
                  <>
                    <Download className="w-3.5 h-3.5" />
                    <span>Unduh File Excel</span>
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function UserRow({ user, isExpanded, onToggleInline, onOpenDrawer, chatHistory, chatLoading, formatCost }) {
  const st = user.stats || {};
  const hasActivity = st.total_queries > 0;

  return (
    <>
      <tr className={`hover:bg-surface-hover transition-colors ${isExpanded ? "bg-brand-low/30 border-l-3 border-l-brand" : ""}`}>
        <td className="py-3.5 px-4">
          <div className="flex items-center gap-3">
            <div className={`w-8 h-8 rounded-full flex items-center justify-center font-bold text-xs shrink-0 ${hasActivity ? "bg-brand text-white shadow-xs" : "bg-paper text-text-sec border border-ledger-border"}`}>
              {(user.name || user.email || "?")[0].toUpperCase()}
            </div>
            <div className="min-w-0">
              <p className="font-semibold text-text-pri text-xs sm:text-sm truncate">{user.name || "—"}</p>
              <p className="text-[11px] font-mono text-text-sec truncate">{user.email}</p>
              {user.company && <p className="text-[10px] text-text-sec mt-0.5">{user.company}</p>}
            </div>
          </div>
        </td>

        <td className="py-3.5 px-3 text-center font-mono font-bold text-text-pri">{st.total_queries || 0}</td>
        <td className="py-3.5 px-3 text-center font-mono text-text-sec">{st.total_messages || 0}</td>
        <td className="py-3.5 px-3 text-center font-mono text-text-sec">
          {(Number(st.total_tokens || 0) >= 1_000_000)
            ? `${(Number(st.total_tokens || 0) / 1_000_000).toFixed(2)}M`
            : (st.total_tokens || 0).toLocaleString()}
        </td>

        <td className="py-3.5 px-3 text-center font-mono font-bold text-emerald-600 dark:text-emerald-400">
          {st.total_cost_usd > 0 ? formatCost(st.total_cost_usd) : "—"}
        </td>

        <td className="py-3.5 px-3 text-center">
          {(st.thumbs_up > 0 || st.thumbs_down > 0) ? (
            <span className="inline-flex items-center gap-1.5 text-xs font-mono">
              <span className="text-emerald-600 dark:text-emerald-400 inline-flex items-center gap-1 bg-emerald-500/10 px-1.5 py-0.5 rounded border border-emerald-500/20 text-[10px]">
                <ThumbsUp className="w-3 h-3" /> {st.thumbs_up}
              </span>
              <span className="text-rose-600 dark:text-rose-400 inline-flex items-center gap-1 bg-rose-500/10 px-1.5 py-0.5 rounded border border-rose-500/20 text-[10px]">
                <ThumbsDown className="w-3 h-3" /> {st.thumbs_down}
              </span>
            </span>
          ) : (
            <span className="text-text-sec">—</span>
          )}
        </td>

        <td className="py-3.5 px-3">
          <div className="flex flex-wrap gap-1">
            {st.chat_count > 0 && <IntentBadge label="Chat" count={st.chat_count} intent="GENERAL_CHAT" />}
            {st.legal_count > 0 && <IntentBadge label="Legal" count={st.legal_count} intent="LEGAL_SEARCH" />}
            {st.draft_count > 0 && <IntentBadge label="Draft" count={st.draft_count} intent="DOCUMENT_DRAFTING" />}
            {st.calc_count > 0 && <IntentBadge label="Calc" count={st.calc_count} intent="TAX_CALCULATION" />}
            {st.dispute_count > 0 && <IntentBadge label="Dispute" count={st.dispute_count} intent="DISPUTE_RESPONSE" />}
            {st.total_queries === 0 && <span className="text-text-sec text-xs">—</span>}
          </div>
        </td>

        <td className="py-3.5 px-3 text-xs font-mono text-text-sec whitespace-nowrap" title={st.last_chat_at ? new Date(st.last_chat_at).toLocaleString("id-ID") : (st.last_active ? new Date(st.last_active).toLocaleString("id-ID") : "")}>
          {(st.last_chat_at || st.last_active) ? formatRelative(st.last_chat_at || st.last_active) : "—"}
        </td>

        <td className="py-3.5 px-4 text-center">
          <div className="inline-flex items-center gap-1.5">
            <button
              onClick={onOpenDrawer}
              disabled={st.total_messages === 0}
              className={`inline-flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs font-semibold transition ${
                st.total_messages > 0
                  ? "bg-brand text-white hover:bg-brand/90 shadow-xs cursor-pointer"
                  : "bg-paper text-text-sec cursor-not-allowed opacity-50 border border-ledger-border"
              }`}
              title="Buka Reader Transkrip Lengkap"
            >
              <FileText className="w-3.5 h-3.5" />
              <span>Baca</span>
            </button>
            <button
              onClick={onToggleInline}
              disabled={st.total_messages === 0}
              className={`p-1.5 rounded-lg text-xs transition border ${
                isExpanded
                  ? "bg-paper border-brand text-brand"
                  : "bg-paper border-ledger-border text-text-sec hover:text-text-pri hover:border-brand/40"
              } ${st.total_messages === 0 ? "opacity-50 cursor-not-allowed" : "cursor-pointer"}`}
              title={isExpanded ? "Tutup Tampilan Cepat" : "Tampilkan Cepat di Tabel"}
            >
              {isExpanded ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
            </button>
          </div>
        </td>
      </tr>

      {/* Inline Quick Preview */}
      {isExpanded && (
        <tr>
          <td colSpan={9} className="p-0 bg-paper/60">
            <div className="p-5 sm:p-7 border-t border-b border-ledger-border space-y-4">
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 pb-3 border-b border-ledger-border">
                <div className="flex items-center gap-3">
                  <div className="w-9 h-9 rounded-xl bg-brand text-white flex items-center justify-center font-bold text-sm shadow-xs">
                    {(user.name || user.email || "?")[0].toUpperCase()}
                  </div>
                  <div>
                    <div className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-brand-low border border-brand/20 text-brand text-[10px] font-bold uppercase tracking-wider mb-0.5">
                      <Sparkles className="w-3 h-3" />
                      <span>Log Transkrip Konsultasi</span>
                    </div>
                    <h4 className="font-bold text-sm sm:text-base text-text-pri">
                      {user.name || user.email}
                    </h4>
                  </div>
                </div>

                <div className="flex items-center gap-2">
                  <button
                    onClick={onOpenDrawer}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-card-bg border border-ledger-border hover:border-brand/40 text-brand text-xs font-semibold shadow-2xs hover:bg-surface-hover transition"
                  >
                    <Maximize2 className="w-3.5 h-3.5" />
                    <span>Perbesar Layar Penuh</span>
                  </button>
                  <button
                    onClick={onToggleInline}
                    className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-lg bg-card-bg border border-ledger-border text-text-sec hover:text-text-pri text-xs font-medium transition"
                  >
                    <X className="w-3.5 h-3.5" />
                    <span>Tutup</span>
                  </button>
                </div>
              </div>

              {chatLoading ? (
                <div className="flex items-center justify-center py-12">
                  <div className="animate-spin w-8 h-8 border-2 border-brand border-t-transparent rounded-full" />
                </div>
              ) : chatHistory.length === 0 ? (
                <p className="text-center text-text-sec py-10 text-xs italic">Tidak ada riwayat percakapan tercatat</p>
              ) : (
                <div className="max-h-[620px] overflow-y-auto space-y-5 pr-2">
                  {chatHistory.map((msg, i) => (
                    <ConsultationTurnCard key={i} msg={msg} />
                  ))}
                </div>
              )}
            </div>
          </td>
        </tr>
      )}
    </>
  );
}

// ─── Consultation Turn Card ──────────────────────────────────────────────────
function ConsultationTurnCard({ msg }) {
  const [copied, setCopied] = useState(false);
  const isUser = msg.sender === "user";

  const handleCopy = () => {
    navigator.clipboard.writeText(msg.message || "");
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const formattedContent = useMemo(() => {
    return preprocessConsultationText(msg.message || "");
  }, [msg.message]);

  const timestampStr = useMemo(() => {
    if (!msg.created_at) return "";
    return new Date(msg.created_at).toLocaleString("id-ID", {
      dateStyle: "medium",
      timeStyle: "short",
      timeZone: "Asia/Jakarta"
    });
  }, [msg.created_at]);

  if (isUser) {
    const { attachments, textOnly } = parseUserMessageAttachments(msg.message || "");
    return (
      <div className="flex flex-col items-end">
        <div className="w-full max-w-3xl rounded-2xl p-5 shadow-2xs transition bg-brand-low/70 border border-brand/25 text-text-pri">
          <div className="flex items-center justify-between mb-2.5 pb-2 border-b border-brand/20">
            <div className="flex items-center gap-2">
              <span className="w-2 h-2 rounded-full bg-brand"></span>
              <span className="text-[11px] font-mono uppercase tracking-wider font-bold text-brand">
                Pertanyaan Pengguna
              </span>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-[11px] font-mono text-text-sec">{timestampStr}</span>
              <button
                onClick={handleCopy}
                className="p-1 rounded hover:bg-brand/10 text-text-sec hover:text-brand transition"
                title="Salin Pertanyaan"
              >
                {copied ? <Check className="w-3.5 h-3.5 text-emerald-500" /> : <Copy className="w-3.5 h-3.5" />}
              </button>
            </div>
          </div>

          {/* File attachment cards */}
          {attachments.length > 0 && (
            <DocumentAttachmentList attachments={attachments} />
          )}

          {/* User question text */}
          {textOnly && (
            <div 
              style={{ fontFamily: "'IBM Plex Sans', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif" }}
              className={`text-[14.5px] leading-relaxed text-text-pri font-medium whitespace-pre-wrap break-words ${
                attachments.length > 0 ? "pt-2.5 mt-1 border-t border-brand/20" : ""
              }`}
            >
              {textOnly}
            </div>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col items-start">
      <div className="w-full max-w-3xl rounded-2xl p-6 shadow-xs transition bg-card-bg border border-ledger-border border-l-4 border-l-brand text-text-pri min-w-0 overflow-hidden">
        <div className="flex flex-wrap items-center justify-between gap-2 mb-3.5 pb-2.5 border-b border-ledger-border/60">
          <div className="flex items-center gap-2.5">
            <div className="w-6 h-6 rounded-md bg-brand text-white flex items-center justify-center font-bold text-[10px] shadow-xs">
              AI
            </div>
            <span className="text-[11.5px] font-mono uppercase tracking-wider font-bold text-brand">
              TACIA AI ({msg.agent_name || "Specialist"})
            </span>
            {msg.feedback && (
              <span className={`inline-flex items-center gap-1 text-[10px] font-mono px-2 py-0.5 rounded border font-semibold ${
                msg.feedback === 'up'
                  ? "bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 border-emerald-500/20"
                  : "bg-rose-500/10 text-rose-600 dark:text-rose-400 border-rose-500/20"
              }`}>
                {msg.feedback === 'up' ? <ThumbsUp className="w-3 h-3" /> : <ThumbsDown className="w-3 h-3" />}
                {msg.feedback === 'up' ? "Upvoted" : "Downvoted"}
              </span>
            )}
          </div>

          <div className="flex items-center gap-2">
            <span className="text-[11px] font-mono text-text-sec">{timestampStr}</span>
            <button
              onClick={handleCopy}
              className="inline-flex items-center gap-1 px-2 py-1 rounded bg-paper hover:bg-surface-hover border border-ledger-border text-[11px] font-medium text-text-sec hover:text-text-pri transition"
              title="Salin Jawaban"
            >
              {copied ? <Check className="w-3 h-3 text-emerald-500" /> : <Copy className="w-3 h-3" />}
              <span>{copied ? "Tersalin" : "Salin"}</span>
            </button>
          </div>
        </div>

        <div 
          style={{ 
            fontFamily: "'IBM Plex Sans', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
            fontFeatureSettings: "'cv02', 'cv03', 'cv04', 'cv11', 'tnum'"
          }}
          className="text-[14.5px] sm:text-[15px] leading-[1.8] text-text-pri break-words min-w-0 overflow-x-auto"
        >
          <ReactMarkdown 
            remarkPlugins={[remarkGfm, remarkMath]}
            rehypePlugins={[rehypeKatex]}
            components={markdownComponents}
          >
            {formattedContent}
          </ReactMarkdown>
        </div>
      </div>
    </div>
  );
}

// ─── Dedicated Consultation Reader Drawer ─────────────────────────────────────
function ConsultationReaderDrawer({ user, onClose, chatHistory, chatLoading, formatCost }) {
  const [searchTerm, setSearchTerm] = useState("");
  const [senderFilter, setSenderFilter] = useState("all"); // 'all' | 'user' | 'assistant'
  const [sortOrder, setSortOrder] = useState("asc"); // 'asc' (chronological) | 'desc' (latest first)
  const [copiedAll, setCopiedAll] = useState(false);

  // Keyboard Escape listener
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [onClose]);

  // Process and filter messages
  const processedMessages = useMemo(() => {
    let list = [...chatHistory];

    // Order
    if (sortOrder === "asc") {
      list.reverse(); // backend returns newest first (DESC), so reverse for chronological
    }

    // Filter sender
    if (senderFilter === "user") {
      list = list.filter(m => (m.sender || "").toLowerCase() === "user" || (m.sender || "").toLowerCase() === "human");
    } else if (senderFilter === "assistant") {
      list = list.filter(m => ["assistant", "ai", "bot"].includes((m.sender || "").toLowerCase()));
    }

    // Filter keyword
    if (searchTerm.trim()) {
      const q = searchTerm.toLowerCase();
      list = list.filter(m => (m.message || "").toLowerCase().includes(q));
    }

    return list;
  }, [chatHistory, sortOrder, senderFilter, searchTerm]);

  const handleCopyAll = () => {
    const fullTranscript = processedMessages.map(m => {
      const who = m.sender === "user" ? "PENGGUNA" : `TACIA AI (${m.agent_name || "Specialist"})`;
      const time = m.created_at ? new Date(m.created_at).toLocaleString("id-ID") : "";
      return `[${time}] ${who}:\n${m.message}\n-----------------------------`;
    }).join("\n\n");

    navigator.clipboard.writeText(fullTranscript);
    setCopiedAll(true);
    setTimeout(() => setCopiedAll(false), 2200);
  };

  const handleExportUserExcel = () => {
    const safeCell = (val) => {
      if (!val) return '-';
      const str = String(val).trim();
      return str.length > 32000 ? str.slice(0, 32000) + '... [terpotong batas sel Excel]' : str;
    };

    const rows = processedMessages.map(m => {
      const isUser = (m.sender || "").toLowerCase() === "user" || (m.sender || "").toLowerCase() === "human";
      return {
        Pengguna: user.name || user.email,
        Email: user.email,
        Perusahaan: user.company || "-",
        Peran: isUser ? "Pengguna" : "TACIA AI",
        Agen: m.agent_name || "Specialist",
        Waktu: m.created_at ? new Date(m.created_at).toLocaleString("id-ID") : "-",
        Feedback: m.feedback || "-",
        Pesan: safeCell(m.message)
      };
    });

    const worksheet = XLSX.utils.json_to_sheet(rows);
    worksheet['!cols'] = [
      { wch: 18 }, { wch: 24 }, { wch: 18 }, { wch: 14 },
      { wch: 14 }, { wch: 20 }, { wch: 10 }, { wch: 80 }
    ];

    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Riwayat Pengguna");
    const safeName = (user.name || user.email || "user").replace(/[^a-z0-9]/gi, "_").toLowerCase();
    XLSX.writeFile(workbook, `Tacia_Transcript_${safeName}_${new Date().toISOString().slice(0,10)}.xlsx`);
  };

  const st = user.stats || {};

  return (
    <div className="fixed inset-0 z-50 overflow-hidden flex justify-end bg-black/60 backdrop-blur-xs transition-opacity animate-in fade-in duration-150">
      <div 
        className="w-full max-w-4xl bg-card-bg h-full shadow-2xl flex flex-col border-l border-ledger-border animate-in slide-in-from-right duration-200"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Drawer Header */}
        <div className="p-5 sm:p-6 border-b border-ledger-border bg-paper flex flex-col gap-4">
          <div className="flex items-start justify-between gap-4">
            <div className="flex items-center gap-3.5">
              <div className="w-12 h-12 rounded-2xl bg-brand text-white flex items-center justify-center font-bold text-lg shadow-sm">
                {(user.name || user.email || "?")[0].toUpperCase()}
              </div>
              <div>
                <div className="flex items-center gap-2">
                  <h3 className="text-lg sm:text-xl font-bold text-text-pri tracking-tight">
                    {user.name || "Pengguna Tanpa Nama"}
                  </h3>
                  {user.company && (
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-paper border border-ledger-border text-[11px] font-semibold text-text-sec">
                      <Building className="w-3 h-3 text-text-sec" />
                      {user.company}
                    </span>
                  )}
                </div>
                <p className="text-xs font-mono text-text-sec mt-0.5">{user.email}</p>
              </div>
            </div>

            <div className="flex items-center gap-2">
              <button
                onClick={handleCopyAll}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-xl bg-card-bg border border-ledger-border hover:border-brand/40 text-text-pri text-xs font-semibold shadow-2xs hover:bg-surface-hover transition"
                title="Salin Semua Transkrip ke Clipboard"
              >
                {copiedAll ? <Check className="w-3.5 h-3.5 text-emerald-500" /> : <Copy className="w-3.5 h-3.5 text-brand" />}
                <span>{copiedAll ? "Tersalin!" : "Salin Semua"}</span>
              </button>

              <button
                onClick={handleExportUserExcel}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-xl bg-card-bg border border-ledger-border hover:border-brand/40 text-text-pri text-xs font-semibold shadow-2xs hover:bg-surface-hover transition"
                title="Unduh Riwayat Pengguna Ini (Excel)"
              >
                <Download className="w-3.5 h-3.5 text-brand" />
                <span>Unduh XLS</span>
              </button>

              <button
                onClick={onClose}
                className="p-2 rounded-xl bg-card-bg border border-ledger-border text-text-sec hover:text-text-pri hover:bg-surface-hover transition"
                title="Tutup (Esc)"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
          </div>

          {/* Quick Metrics Bar */}
          <div className="flex flex-wrap items-center gap-2 pt-2 border-t border-ledger-border/40 text-xs font-mono">
            <span className="px-2.5 py-1 rounded-lg bg-paper border border-ledger-border text-text-pri">
              <span className="text-text-sec">Total Kueri:</span> <strong>{st.total_queries || 0}</strong>
            </span>
            <span className="px-2.5 py-1 rounded-lg bg-paper border border-ledger-border text-text-pri">
              <span className="text-text-sec">Pesan:</span> <strong>{st.total_messages || 0}</strong>
            </span>
            <span className="px-2.5 py-1 rounded-lg bg-paper border border-ledger-border text-text-pri">
              <span className="text-text-sec">Token:</span> <strong>{(st.total_tokens || 0).toLocaleString()}</strong>
            </span>
            <span className="px-2.5 py-1 rounded-lg bg-paper border border-ledger-border text-emerald-600 dark:text-emerald-400 font-bold">
              <span className="text-text-sec font-normal">Biaya:</span> {st.total_cost_usd ? formatCost(st.total_cost_usd) : "$0.00"}
            </span>
            {(st.thumbs_up > 0 || st.thumbs_down > 0) && (
              <span className="px-2.5 py-1 rounded-lg bg-paper border border-ledger-border inline-flex items-center gap-2">
                <span className="text-emerald-600 dark:text-emerald-400 inline-flex items-center gap-1">
                  <ThumbsUp className="w-3 h-3" /> {st.thumbs_up || 0}
                </span>
                <span className="text-rose-600 dark:text-rose-400 inline-flex items-center gap-1">
                  <ThumbsDown className="w-3 h-3" /> {st.thumbs_down || 0}
                </span>
              </span>
            )}
          </div>
        </div>

        {/* Toolbar: Search, Filters, Sort Order */}
        <div className="px-5 py-3 border-b border-ledger-border bg-paper/50 flex flex-col sm:flex-row items-center justify-between gap-3">
          <div className="relative flex-1 w-full">
            <Search className="w-3.5 h-3.5 absolute left-3 top-1/2 -translate-y-1/2 text-text-sec" />
            <input
              type="text"
              placeholder="Cari kata kunci dalam riwayat konsultasi..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-9 pr-3 py-1.5 rounded-lg border border-ledger-border bg-input-bg text-xs text-text-pri placeholder:text-muted focus:outline-none focus:ring-1 focus:ring-brand shadow-2xs"
            />
            {searchTerm && (
              <button
                onClick={() => setSearchTerm("")}
                className="absolute right-2.5 top-1/2 -translate-y-1/2 text-text-sec hover:text-text-pri"
              >
                <X className="w-3 h-3" />
              </button>
            )}
          </div>

          <div className="flex items-center gap-2 w-full sm:w-auto justify-between sm:justify-end">
            <div className="flex items-center bg-card-bg border border-ledger-border rounded-lg p-0.5 text-[11px] font-medium shadow-2xs">
              <button
                onClick={() => setSenderFilter("all")}
                className={`px-2.5 py-1 rounded-md transition ${senderFilter === "all" ? "bg-brand text-white font-semibold shadow-xs" : "text-text-sec hover:text-text-pri"}`}
              >
                Semua
              </button>
              <button
                onClick={() => setSenderFilter("user")}
                className={`px-2.5 py-1 rounded-md transition ${senderFilter === "user" ? "bg-brand text-white font-semibold shadow-xs" : "text-text-sec hover:text-text-pri"}`}
              >
                Tanya
              </button>
              <button
                onClick={() => setSenderFilter("assistant")}
                className={`px-2.5 py-1 rounded-md transition ${senderFilter === "assistant" ? "bg-brand text-white font-semibold shadow-xs" : "text-text-sec hover:text-text-pri"}`}
              >
                Jawab
              </button>
            </div>

            <button
              onClick={() => setSortOrder(prev => prev === "asc" ? "desc" : "asc")}
              className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-lg bg-card-bg border border-ledger-border text-[11px] font-medium text-text-sec hover:text-text-pri shadow-2xs transition"
              title="Ubah Urutan Waktu"
            >
              <ArrowUpDown className="w-3 h-3 text-brand" />
              <span>{sortOrder === "asc" ? "Lama ke Baru" : "Baru ke Lama"}</span>
            </button>
          </div>
        </div>

        {/* Drawer Scrollable Body */}
        <div className="flex-1 overflow-y-auto p-5 sm:p-8 space-y-6 bg-paper/30">
          {chatLoading ? (
            <div className="flex flex-col items-center justify-center py-24 gap-3">
              <div className="animate-spin w-8 h-8 border-3 border-brand border-t-transparent rounded-full" />
              <span className="text-xs text-text-sec font-medium">Memuat riwayat konsultasi...</span>
            </div>
          ) : processedMessages.length === 0 ? (
            <div className="text-center py-20">
              <div className="w-12 h-12 rounded-full bg-paper border border-ledger-border text-text-sec flex items-center justify-center mx-auto mb-3">
                <MessageSquare className="w-6 h-6" />
              </div>
              <h4 className="text-sm font-bold text-text-pri">Tidak Ada Percakapan Ditemukan</h4>
              <p className="text-xs text-text-sec mt-1 max-w-sm mx-auto">
                {searchTerm ? `Tidak ada pesan yang memuat kata kunci "${searchTerm}"` : "Pengguna ini belum memulai percakapan dengan TACIA."}
              </p>
            </div>
          ) : (
            <div className="max-w-3xl mx-auto space-y-6">
              {processedMessages.map((msg, i) => (
                <ConsultationTurnCard key={i} msg={msg} />
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function IntentBadge({ label, count, intent }) {
  const cfg = INTENT_BADGES[intent] || { label, color: "bg-paper text-text-sec border-ledger-border" };
  return (
    <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded border text-[9px] font-semibold uppercase tracking-wider ${cfg.color}`}>
      {cfg.label} <span className="opacity-75 font-mono font-bold">{count}</span>
    </span>
  );
}

function formatRelative(dateStr) {
  const now = new Date();
  const date = new Date(dateStr);
  const diffMs = now - date;
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMins < 1) return "Baru saja";
  if (diffMins < 60) return `${diffMins}m lalu`;
  if (diffHours < 24) return `${diffHours}j lalu`;
  if (diffDays < 7) return `${diffDays}h lalu`;
  return date.toLocaleDateString("id-ID", { day: 'numeric', month: 'short' });
}
